import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ReviewForm() {
  const [canReview, setCanReview] = useState(false);
  const [already, setAlready] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [showName, setShowName] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/my/can-review")
      .then(({ data }) => { setCanReview(data.can_review); setAlready(data.already_reviewed); })
      .catch(() => {});
  }, []);

  if (!canReview) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/reviews", { rating, text, show_name: showName, show_email: showEmail });
      toast.success("Thanks for your review!");
      setOpen(false);
      setAlready(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button data-testid="write-review-btn" onClick={() => setOpen(true)} variant="ghost"
        className="mt-3 rounded-full border border-white/15 text-zinc-200 hover:bg-white/5 hover:text-white">
        <Star className="mr-2 h-4 w-4" /> {already ? "Update your review" : "Write a review"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-panel max-w-md space-y-4 rounded-2xl border-white/10 text-white">
          <DialogTitle className="font-display text-xl">Rate your experience</DialogTitle>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" data-testid={`review-star-${s}`}
                onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)}>
                <Star className={`h-7 w-7 transition-colors ${s <= (hover || rating) ? "fill-primary text-primary" : "text-zinc-700"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-text" rows={4} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Tell us how it went…" className="rounded-sm border-white/10 bg-black" />
          <div className="flex items-center justify-between">
            <Label className="text-sm text-zinc-300">Show my name</Label>
            <Switch data-testid="review-show-name" checked={showName} onCheckedChange={setShowName} />
          </div>
          <p className="text-xs text-zinc-500">
            {showName ? "Your name will be shown on the review." : "You'll appear as \"Anonymous\"."}
          </p>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-zinc-300">Show my email</Label>
            <Switch data-testid="review-show-email" checked={showEmail} onCheckedChange={setShowEmail} />
          </div>
          <Button data-testid="review-submit-btn" onClick={submit} disabled={saving || !text.trim()}
            className="w-full rounded-full bg-primary text-white hover:bg-primary/90">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit review
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
